<script setup lang="ts">
import translaters from "@/tradutors/assine";
import api from "@/api";
import { corteLigado, funilCadastro, funilErroDaApi, funilErroTecnico, funilLogin } from "@/utils/eventosDoFunil";
import type { CredentialResponse } from "vue3-google-signin";
import { decodeCredential, useOneTap } from "vue3-google-signin";
import ModalAccessRestricted from "@/components/Modais/ModalAccessRestricted.vue";
const router = useRouter();

const storeUser = userStore();
const storeJwt = jwtStore();

const isLoggedIn = computed(() => storeUser.isLoggedIn);
const loading = ref(false);
const showRestrictedModal = ref(false);
const restrictedModalHtml = ref("");

function handleRestricted(response: any): boolean {
  if (response?.status === 423 && response?.data?.status === "restricted") {
    restrictedModalHtml.value = response.data.modal_html || "";
    showRestrictedModal.value = true;
    loading.value = false;
    return true;
  }
  return false;
}
const emit = defineEmits(["returnMessage", "closeModal", "isLoginUser"]);
const props = defineProps(["redirect", "size"]);
const route = useRoute();

const form = ref<IForm>({
  firstName: "",
  lastName: "",
  email: "",
  emailAgain: "",
  anotherEmail: "",
  country: "33",
  password: "",
  passwordAgain: "",
  emailFacebook: "",
  emailGoogle: "",
  acept: true,
  token: "",
  credential: "",
});

const reactiveLocale = computed(() => route.params.language); // Certifique-se de que locale é uma ref

const translate = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return translaters.portugues;
  } else {
    return translaters.ingles;
  }
});

/** @param medido o erro veio da API e já foi classificado (erro_tipo) */
function error(value: string, medido = false) {
  emit("returnMessage", value);
  // Depois do corte: erro sem a mensagem, classificado.
  if (corteLigado()) {
    // O que não veio da API é falha do Google Sign-In (One Tap, credencial).
    if (!medido) funilErroTecnico("login", "google_signin");
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "erro",
    erro: {
      tipo: value,
    },
  });
}
function redirectSocial(name: string) {
  router.push({
    name,
    params: {
      language: reactiveLocale.value,
    },
    query: {
      ...route.query,
    },
  });
}

interface IForm {
  email: string;
  password: string;
  emailAgain: string;
  passwordAgain: string;
  firstName: string;
  lastName: string;
  country: string;
  acept: boolean;
  token: string;
  anotherEmail: string;
  emailFacebook: string;
  emailGoogle: string;
  credential: string;
}

// A conta criada pelo Google entra pelo Google; esta senha só cumpre a validação do cadastro.
function senhaAleatoria(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function onSuccessSocial(response: CredentialResponse) {
  let dataEmail: string | null = null;
  let dataCompany = "google";
  if (response.credential) {
    // GOOGLE
    var dataSocial = decodeCredential(response.credential);
    dataEmail = dataSocial.email;
    var dataFirstName = dataSocial.given_name;
    var dataLastName = dataSocial.family_name;
    var dataEmailFacebook: any = null;
    var dataEmailGoogle = dataSocial.email;
    dataCompany = "google";
  }

  if (!dataEmail) {
    error("Erro Login");
    return false;
  }

  // O servidor confere o token assinado pelo Google; o e-mail sozinho não autentica.
  let request = {
    email: dataEmail,
    company: dataCompany,
    language: reactiveLocale.value,
    credential: response.credential,
  };
  loading.value = true;
  api
    .post("v1/subscribe/checkLoginEmailSocial", request)
    .then(({ data }) => {
      if (!data.process) {
        funilErroDaApi("login", data, 200);
        error(data.errors, true);
        loading.value = false;
        return false;
      }
      if (data.login) {
        // Conta que já existia: login. (O newUser daqui é "nunca teve pedido", não conta nova.)
        funilLogin(dataCompany);
        storeUser.setUser(data.login.user);
        storeJwt.setJwt(data.login.jwt);
        storeUser.setNewUser(data.login.newUser);
        if (route.name != "language-assine-account" && data.login.newUser) {
          emit("closeModal");
          redirectSocial("language-freetour");
        } else if (props.redirect) {
          emit("closeModal");
          redirectSocial(props.redirect);
        } else {
          storeUser.getActiveUser().then((isActive) => {
            if (isActive) window.location.reload();
            else emit("isLoginUser");
            loading.value = false;
          });
        }
      } else {
        form.value.firstName = dataFirstName;
        form.value.lastName = dataLastName;
        form.value.email = dataEmail;
        form.value.emailAgain = dataEmail;
        form.value.anotherEmail = "";
        form.value.country = reactiveLocale.value == "pt-br" ? "33" : "69";
        const senha = senhaAleatoria();
        form.value.password = senha;
        form.value.passwordAgain = senha;
        form.value.emailFacebook = dataEmailFacebook;
        form.value.emailGoogle = dataEmailGoogle;
        form.value.token = "true";
        form.value.credential = response.credential;
        api
          .post("v1/subscribe/storeCreateUserAssine", form.value)
          .then(({ data }) => {
            if (!data.process) {
              funilErroDaApi("cadastro", data, 200);
              error(data.errors, true);
            }
            if (data.login) {
              // Sem conta para o e-mail do Google: a conta nasce agora.
              funilCadastro(dataCompany);
              storeUser.setUser(data.login.user);
              storeJwt.setJwt(data.login.jwt);
              if (route.name != "language-assine-account") {
                emit("closeModal");
                redirectSocial("language-freetour");
              } else if (props.redirect) {
                emit("closeModal");
                redirectSocial(props.redirect);
              } else {
                emit("isLoginUser");
              }
            }
          })
          .catch((err) => {
            if (handleRestricted(err?.response)) return;
            funilErroDaApi("cadastro", err?.response?.data, err?.response?.status);
            error(err?.response?.data?.errors, true);
          })
          .finally(() => (loading.value = false));
      }
    })
    .catch((err) => {
      if (handleRestricted(err?.response)) return;
      funilErroDaApi("login", err?.response?.data, err?.response?.status);
      error(err?.response?.data?.errors, true);
    })
    .finally(() => {
      loading.value = false;
    });
}
function onFailureSocial() {
  error("Login failed");
}

if (!isLoggedIn.value) {
  useOneTap({
    onSuccess: (response: CredentialResponse) => {
      onSuccessSocial(response);
    },
    onError: () => error("Erro One Tap Login"),
  });
}
</script>
<template>
  <div class="text-center mt-3 mb-5" v-if="loading">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
  <div class="row caixa-social" v-else>
    <div class="col-12 text-login">
      {{
        route.name == "language-minhaconta"
          ? translate.loginText1
          : translate.loginText
      }}
    </div>
    <div class="col-12">
      <GoogleSignInButton
        class="col-12 d-flex justify-content-center"
        @success="onSuccessSocial"
        @error="onFailureSocial"
        theme="outline"
        shape="circle"
        :width="props.size"
      ></GoogleSignInButton>
    </div>
  </div>
  <ModalAccessRestricted
    :showModal="showRestrictedModal"
    :htmlContent="restrictedModalHtml"
    @close="showRestrictedModal = false"
  />
</template>

<style scoped lang="css">
.button-facebook {
  color: #ffffff;
  font-family: "Exo-2";
  display: flex;
  align-items: center;
  border-radius: 5px;
}

.text-login {
  text-align: center;
  font-family: "Exo-2";
  font-size: 14px;
  line-height: 21px;
  color: #707070;
  padding-bottom: 5px;
}

/* Tema dourado: a classe .gold-theme vem da raiz .slot-body da página de
   assinatura, por isso o seletor casa mesmo com o <style> scoped daqui. */
.gold-theme .text-login {
  color: #c2c2c2;
}

:deep(.g-btn-wrapper) {
  display: inherit !important;
}

.fb-button {
  background-color: #4267b2;
  cursor: pointer;
  font-weight: 900;
  font-size: 14px;
  border-radius: 5px;
  height: 40px;
}

.fb-button img {
  margin-left: 3px;
  border-radius: 3px;
}

@media screen and (min-width: 770px) {
  .caixa-social {
    height: 84px;
  }
}

/* regras mobile 769px */
@media screen and (max-width: 769px) {
  .text-login {
    text-align: center;
    padding-left: 0px;
    padding-bottom: 5px;
  }
}
</style>
